import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Header, Paragraph } from "components/atoms/Typography";
import "./ChartTooltip.css";

export interface ChartTooltipProps {
    title: string | number;
    subtitle?: string | number;
    rows: TooltipRow[];
}

export type TooltipRow = {
    unit: string;
    value: string | number;
    label: string;
    color?: string;
};

function ChartTooltip({ title, subtitle, rows }: ChartTooltipProps) {
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            setMousePos({ x: e.clientX, y: e.clientY });
        };
        document.addEventListener("mousemove", handleMouseMove);
        return () => document.removeEventListener("mousemove", handleMouseMove);
    }, []);

    const formatValueWithUnit = (value: string | number, unit: string) => {
        if (unit.includes("$")) {
            const cleanUnit = unit.replace("$", "").trim();

            const hasNegativeValue = typeof value === "string" && value.includes("-");

            const cleanValue = hasNegativeValue ? value.replace("-", "") : value;

            return `${hasNegativeValue ? "-" : ""}$${cleanValue}${cleanUnit ? ` ${cleanUnit}` : ""}`;
        }
        return `${value} ${unit}`;
    };

    const left = Math.max(10, Math.min(mousePos.x - 175, window.innerWidth - 360));
    const top = mousePos.y - 14;

    // We need a portal here, so the tooltip renders outside of ancestors, preventing being
    // displayed outside the window
    return createPortal(
        <div
            className="chart-tooltip"
            style={{
                position: "fixed",
                left,
                top,
                transform: "translateY(-100%)",
                pointerEvents: "none",
                zIndex: 9999,
            }}
        >
            <Header>{title}</Header>
            {subtitle && <Paragraph>{subtitle}</Paragraph>}
            {rows.map(({ label, color, unit, value }) => (
                <div className="chart-tooltip__row" key={label}>
                    {color && (
                        <div
                            className="chart-tooltip__row__color-key"
                            style={{
                                backgroundColor: color,
                            }}
                        />
                    )}
                    <Paragraph style={{ color: "var(--audette-gray-500" }}>{label}</Paragraph>
                    <Paragraph>{formatValueWithUnit(value, unit)}</Paragraph>
                </div>
            ))}
        </div>,
        document.body
    );
}

export default ChartTooltip;
